import {Component, OnDestroy, OnInit} from "@angular/core";
import {Store} from "@ngrx/store";
import {
  WireForm,
  WireTransactionDetails
} from "projects/money-movement-ui/src/app/create-request/models/form/TransactionForm";
import {
  selectStagedTransactionForm,
  selectWireCountries,
  selectWireInfo
} from "projects/money-movement-ui/src/app/create-request/reducers";
import {
  FormatIsoAddressFieldsService
} from "projects/money-movement-ui/src/app/shared/services/format-isoAddressFields.service";
import {
  Constants,
  recipientDetailsWireLabel,
  recipientType
} from "projects/money-movement-ui/src/app/shared/utilities/Constants";
import {
  CountryDescriptionFromCode
} from "projects/money-movement-ui/src/app/shared/utilities/CountryDescriptionFromCode";
import {combineLatest, Subject, takeUntil} from "rxjs";

@Component({
  standalone: false,
  selector: 'mm-recipient-details-wires-condensed',
  templateUrl: './recipient-details-wires-condensed.component.html',
  styleUrls: ['./recipient-details-wires-condensed.component.scss']
})
export class RecipientDetailsWiresCondensedComponent implements OnInit, OnDestroy {
  readonly labels = recipientDetailsWireLabel;
  readonly subtitle = 'Recipient details';

  recipientAccountNumber: string = '';
  recipientAccountNumberLabel: string = this.labels.recipientAccountNumber;
  recipientType: string = ''
  recipientName: string = '';
  recipientAddress: string[] = [];
  isEscrow: boolean = false;
  useSloa: boolean = false;

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private store: Store, private formatIsoAddressFieldsService: FormatIsoAddressFieldsService) {
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(selectStagedTransactionForm),
      this.store.select(selectWireInfo),
      this.store.select(selectWireCountries)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([form, info, countries]) => {
      const details = form.transactionDetails as WireTransactionDetails;
      let countryMap = countries.map((country) => {
        return {value: country.code, name: country.description};
      });
      this.useSloa = (form as WireForm).extraPrompts.useStandingAuth.toLowerCase() === 'yes' || (form as WireForm).extraPrompts.useBundledStandingAuth === 'yes' ? true : false;
      const isRenderISOProperties = details.isISOCompliant || !this.useSloa;
      const isIrsAba = info.isIrsAba || false;
      this.isEscrow = details.isEscrow || false;
      this.recipientAccountNumberLabel = details.receivingAccountHolderIdentifierList?.[0]?.identifierType?.toLowerCase() === 'iban'
        ? this.labels.recipientAccountIBAN
        : this.labels.recipientAccountNumber;
      this.recipientAccountNumber = isIrsAba ? 'N/A' : details.toAccount || '';
      this.recipientType = this.determineRecipientType(details.recipient || '');
      this.recipientName = isRenderISOProperties
        ? details.name
        : details?.receivingAccountHolderName1 + (details?.receivingAccountHolderName2 == undefined ? '' : ' ') + (details?.receivingAccountHolderName2 ?? '');
      const recipientCountry = details.receivingAccountHolderCountry.length == 2 ? CountryDescriptionFromCode(details.receivingAccountHolderCountry, countryMap) : details.receivingAccountHolderCountry;
      this.recipientAddress = isRenderISOProperties
        ? this.getIsoAddress(details, recipientCountry)
        : this.getNonIsoAddress(details?.receivingAccountHolderAddress1, details?.receivingAccountHolderAddress2, recipientCountry);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  determineRecipientType(recipient: string): string {
    if (recipient === recipientType.firstParty) {
      return Constants.wireFirstParty;
    }
    if (this.isEscrow) {
      return Constants.wireThirdPartyEscrow;
    }
    return Constants.wireThirdParty
  }

  getNonIsoAddress(address1?: string, address2?: string, country?: string): string[] {
    let address = [];
    if (address1) {
      address.push(address1);
    }
    if (address2) {
      address.push(address2);
    }
    if (country) {
      address.push(country);
    }
    return address;
  }

  getIsoAddress(details: WireTransactionDetails, receipientCountry: string): string[] {
    let receivingAddress = [];
    receivingAddress.push(this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderStreetNumber ? details.receivingAccountHolderStreetNumber + " " + details.receivingAccountHolderStreetName : details.receivingAccountHolderStreetName));
    receivingAddress.push(
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderApartmentSuiteEtc, details.receivingAccountHolderDepartment, details.receivingAccountHolderBuildingName, details.receivingAccountHolderFloor) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderDepartment, details.receivingAccountHolderBuildingName, details.receivingAccountHolderFloor) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderBuildingName, details.receivingAccountHolderFloor) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderFloor, ""));
    receivingAddress.push(
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderTownLocationName, details.receivingAccountHolderCity, details.receivingAccountHolderDistrict, details.receivingAccountHolderState, details.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderCity, details.receivingAccountHolderDistrict, details.receivingAccountHolderState, details.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderDistrict, details.receivingAccountHolderState, details.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderState, details.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingAccountHolderZip));
    receivingAddress.push(this.formatIsoAddressFieldsService.getFieldsValue(receipientCountry));
    return receivingAddress;
  }
}
